import type { Stage } from 'modules/client/tournaments/domain/types';
import getYearText from 'modules/client/tournaments/domain/utils/get-year-text';
import Header from 'modules/client/tournaments/ui/components/header';
import StageSelector from 'modules/client/tournaments/ui/components/stage-selector';
import TournamentBracketTree from 'modules/client/tournaments/ui/components/tournament-bracket-tree';
import useChampionshipInfo from 'modules/client/tournaments/ui/hooks/use-championship';
import FixturesSection from 'modules/client/tournaments/ui/pages/tabs/fixtures-section';
import StageGroupsSection from 'modules/client/tournaments/ui/pages/tabs/stage-groups-section';
import StageStandingsTable from 'modules/client/tournaments/ui/pages/tabs/stage-standings-table';
import TeamsSection from 'modules/client/tournaments/ui/pages/tabs/teams-section';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';

function ClientChampionshipPage() {
  const { idChampionship } = useParams();
  const { players, stages, teams, matches, championship, loaded } = useChampionshipInfo({
    championshipId: Number(idChampionship),
  });

  const [activeTab, setActiveTab] = useState('stages');
  const [activeStage, setActiveStage] = useState(1);

  useEffect(() => {
    if (!loaded) {
      return;
    }
    setActiveStage(stages[0].id);
  }, [loaded, stages]);

  if (!loaded) {
    return (
      <div className="flex-center h-dvh w-dvw flex-col">
        <ClipLoader />
        <span>Загрузка...</span>
      </div>
    );
  }

  const currentStage: Stage | undefined = stages.find((stage) => stage.id === activeStage) || stages[0];

  // Filter data by the current stage
  const stageMatches = matches.filter((match) => match.stageId === activeStage);
  const stagePlayers = players;

  // Convert stage teams to legacy format for components that expect Team[]
  const stageTeamsAsTeams =
    currentStage?.teams.map((stageTeam) => {
      const baseTeam = teams.find((t) => t.id === stageTeam.teamId)!;
      return {
        ...baseTeam,
        ...stageTeam,
        id: baseTeam.id,
      };
    }) ?? [];

  const renderContent = () => {
    switch (activeTab) {
      case 'stages':
        switch (currentStage?.type) {
          case 'ROUND_ROBIN_1':
          case 'ROUND_ROBIN_2':
            return <StageStandingsTable stage={currentStage} teams={teams} />;
          case 'DOUBLE_ELIMINATION':
          case 'SINGLE_ELIMINATION':
            return <TournamentBracketTree brackets={currentStage?.bracketMatches ?? []} stageId={currentStage?.id} teams={teams} />;
          case 'GROUP':
            return <StageGroupsSection stage={currentStage} teams={teams} />;
          default:
            return <StageStandingsTable stage={currentStage} teams={teams} />;
        }
      case 'fixtures':
        return <FixturesSection matches={stageMatches} />;
      case 'teams':
        return <TeamsSection teams={stageTeamsAsTeams} players={stagePlayers} />;
      default:
        return <StageStandingsTable stage={currentStage} teams={teams} />;
    }
  };

  const yearText = getYearText(championship?.startDate, championship?.endDate);
  const currentYear = new Date().getFullYear();

  return (
    <div className="flex h-full min-h-dvh flex-col bg-gray-100">
      <Header activeTab={activeTab} setActiveTab={setActiveTab} championship={championship} />

      <main className="w-full flex-1">
        <div className="mx-auto flex max-w-7xl flex-1 flex-col px-4 py-8 sm:px-6 lg:px-8">
          <StageSelector stages={stages} activeStage={activeStage} onStageChange={setActiveStage} />
          {renderContent()}
        </div>
      </main>

      <div className="" />

      <footer className="mt-16 border-t border-gray-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="mb-4 flex items-center justify-center space-x-2">
              <span className="text-2xl">⚽</span>
              <span className="text-lg font-bold text-gray-900">{championship?.tournament.name ?? 'Неизвестный организатор'}</span>
            </div>
            <p className="text-sm text-gray-600">
              {championship?.name ?? 'Неизвестный турнир'} {yearText} • Объединяем команды через футбол
            </p>
            <div className="mt-4 text-xs text-gray-500">© {currentYear} Roowix. Все права защищены.</div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default ClientChampionshipPage;
